// Avg Scroll Depth, derived from GA4 scroll / scroll_end / page_view events.
// See the companion test for the exact bucket weighting.

// Labels used in the scroll depth warnings, keyed like the rowCounts fields.
const SCROLL_ROW_LABELS = {
  page_view: "page_view",
  scroll25: "scroll@25",
  scroll50: "scroll@50",
  scroll75: "scroll@75",
  scroll100: "scroll@100",
  scrollEnd: "scroll_end",
};

// The count the calculation uses, plus how many rows actually matched. GA4 splits
// query_events rows by outbound_url/outbound/linkUrl too, so a bucket can span several
// rows — but only the first one is counted, which the row count makes visible.
const pickEventCount = (events, predicate) => {
  const matches = events.filter(predicate);

  return {
    value: parseInt(matches[0]?.eventCount, 10) || 0,
    rows: matches.length,
  };
};

const emptyScrollDepthBreakdown = () => ({
  pageViews: 0,
  raw: { n25: 0, n50: 0, n75: 0, n90: 0, n100: 0 },
  rowCounts: {
    page_view: 0,
    scroll25: 0,
    scroll50: 0,
    scroll75: 0,
    scroll100: 0,
    scrollEnd: 0,
  },
  buckets: { b25: 0, b50: 0, b75: 0, b90: 0, b100: 0 },
  warnings: [],
  result: 0,
});

const scrollDepthWarnings = (raw, rowCounts, pageViews, result) => {
  const warnings = [];

  Object.entries(rowCounts).forEach(([key, count]) => {
    if (count > 1) {
      warnings.push(
        `${SCROLL_ROW_LABELS[key]} matched ${count} rows, only the first is counted`,
      );
    }
  });

  // Scroll buckets are cumulative, so counts must fall as the depth rises. The Math.abs
  // below turns any inversion into a positive contribution, inflating the average.
  const levels = ["n25", "n50", "n75", "n90", "n100"];
  levels.slice(0, -1).forEach((shallow, i) => {
    const deep = levels[i + 1];
    if (raw[deep] > raw[shallow]) {
      warnings.push(
        `non-monotonic: ${deep} (${raw[deep]}) > ${shallow} (${raw[shallow]})`,
      );
    }
  });

  if (pageViews === 0) {
    warnings.push("no page_view events, result forced to 0");
  }

  if (result > 100) {
    warnings.push(`result exceeds 100% (${result})`);
  }

  return warnings;
};

// Every intermediate value behind Avg Scroll Depth, so the aggregation can be inspected
// in the admin "Data Transformed" panel and asserted in tests instead of being a single
// opaque number. scroll_end is treated as the 90% bucket.
export const avgScrollDepthBreakdown = (events) => {
  if (!Array.isArray(events)) {
    return emptyScrollDepthBreakdown();
  }

  const isScrollAt = (percent) => (e) =>
    e.eventName === "scroll" && e.percentScrolled === percent;

  const pageView = pickEventCount(events, (e) => e.eventName === "page_view");
  const scroll25 = pickEventCount(events, isScrollAt("25"));
  const scroll50 = pickEventCount(events, isScrollAt("50"));
  const scroll75 = pickEventCount(events, isScrollAt("75"));
  const scroll100 = pickEventCount(events, isScrollAt("100"));
  const scrollEnd = pickEventCount(events, (e) => e.eventName === "scroll_end");

  const raw = {
    n25: scroll25.value,
    n50: scroll50.value,
    n75: scroll75.value,
    n90: scrollEnd.value,
    n100: scroll100.value,
  };

  const rowCounts = {
    page_view: pageView.rows,
    scroll25: scroll25.rows,
    scroll50: scroll50.rows,
    scroll75: scroll75.rows,
    scroll100: scroll100.rows,
    scrollEnd: scrollEnd.rows,
  };

  const buckets = {
    b100: 100 * raw.n100,
    b90: 90 * Math.abs(raw.n90 - raw.n100),
    b75: 75 * Math.abs(raw.n75 - raw.n90),
    b50: 50 * Math.abs(raw.n50 - raw.n75),
    b25: 25 * Math.abs(raw.n25 - raw.n50),
  };

  const pageViews = pageView.value;
  const result =
    pageViews === 0
      ? 0
      : (buckets.b100 + buckets.b90 + buckets.b75 + buckets.b50 + buckets.b25) /
        pageViews;

  return {
    pageViews,
    raw,
    rowCounts,
    buckets,
    warnings: scrollDepthWarnings(raw, rowCounts, pageViews, result),
    result,
  };
};

export const avgScrollDepth = (events) => avgScrollDepthBreakdown(events).result;
